import { useState, useEffect, useRef, useCallback } from 'react'
import { Chart, registerables } from 'chart.js'
import { api } from '../api'
import { token } from '../tokens'
import { useI18n } from '../i18n'
import { ErrorState, errorMessage } from './ErrorState'
import type { AlertTimeSeries } from '../types'

Chart.register(...registerables)

interface Props {
  days: number
}

export function AlertsChart({ days }: Props) {
  const { t, fmt } = useI18n()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const chartRef = useRef<Chart | null>(null)
  const [data, setData] = useState<AlertTimeSeries[]>([])
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setData(await api.getTimeSeries(days))
      setError(null)
    } catch (err: unknown) {
      setError(errorMessage(err))
      setData([])
    }
  }, [days])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!canvasRef.current || data.length === 0) return

    chartRef.current?.destroy()

    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: {
        labels: data.map((d) => fmt.dayShort(d.date)),
        // Canvas ne resout pas les variables CSS : on lit la valeur du token.
        datasets: [
          { label: 'CRITICAL', data: data.map((d) => d.critical), backgroundColor: token('--crit'), stack: 's', borderRadius: 3 },
          { label: 'WARNING', data: data.map((d) => d.warning), backgroundColor: token('--warn'), stack: 's', borderRadius: 3 },
          { label: 'OK', data: data.map((d) => d.ok), backgroundColor: token('--ok-fg'), stack: 's', borderRadius: 3 },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: token('--text'),
            padding: 10,
            cornerRadius: 6,
            titleFont: { size: 12 },
            bodyFont: { size: 12 },
          },
        },
        scales: {
          // Grille horizontale seulement : les barres se lisent en hauteur.
          x: {
            stacked: true,
            grid: { display: false },
            border: { display: false },
            ticks: { color: token('--text-hint'), font: { size: 11 } },
          },
          y: {
            stacked: true,
            grid: { color: token('--border') },
            border: { display: false },
            ticks: { color: token('--text-hint'), font: { size: 11 }, precision: 0 },
          },
        },
        animation: { duration: 300 },
      },
    })

    return () => {
      chartRef.current?.destroy()
    }
  }, [data, fmt])

  if (error) {
    return (
      <div className="chart-card">
        <div className="chart-title">{t('chart.alertsByLevel', { days })}</div>
        <ErrorState message={error} onRetry={load} compact />
      </div>
    )
  }

  return (
    <div className="chart-card">
      <div className="chart-title">{t('chart.alertsByLevel', { days })}</div>
      <div className="chart-legend">
        <span className="legend-item">
          <span className="legend-dot" style={{ background: 'var(--crit)' }} />
          CRITICAL
        </span>
        <span className="legend-item">
          <span className="legend-dot" style={{ background: 'var(--warn)' }} />
          WARNING
        </span>
        <span className="legend-item">
          <span className="legend-dot" style={{ background: 'var(--ok-fg)' }} />
          OK
        </span>
      </div>
      <div className="chart-container">
        <canvas ref={canvasRef} />
      </div>
    </div>
  )
}
